// ==========================================================================
// components/SpendWidget.tsx — money at a glance on Home (10 Oct 2026)
//   James: "some more visual widgets and graphs overall and on the home page".
//   Left: spending per month for the last 6 months (this month highlighted).
//   Right: this month's biggest budget codes against their budget.
//   Totals come from the database (category_month_totals): reconciled lines
//   and the rental ledger; money movements are left out.
// ==========================================================================
import { Link } from "react-router-dom";
import { addMonths, endOfMonth, format, parseISO, startOfMonth } from "date-fns";
import { Card } from "@/components/ui";
import { money, useCategories } from "@/lib/money";
import { useMonthTotals } from "@/lib/contacts";
import { tokenColour } from "@/lib/calendar";

export default function SpendWidget() {
  const cats = useCategories();
  const now = new Date();
  const from = format(startOfMonth(addMonths(now, -5)), "yyyy-MM-dd"); const to = format(endOfMonth(now), "yyyy-MM-dd");
  const totals = useMonthTotals(from, to);
  const all = cats.data ?? [];
  const kind = (id: string) => all.find((c) => c.id === id)?.kind;
  const months = Array.from({ length: 6 }, (_, i) => format(startOfMonth(addMonths(now, i - 5)), "yyyy-MM-dd"));
  const spend = (m: string) => -(totals.data ?? []).filter((r) => r.month === m && kind(r.category_id) === "expense").reduce((t, r) => t + r.amount_cents, 0);
  const income = (m: string) => (totals.data ?? []).filter((r) => r.month === m && kind(r.category_id) === "income").reduce((t, r) => t + r.amount_cents, 0);
  const max = Math.max(1, ...months.map((m) => Math.max(spend(m), income(m))));
  const thisMonth = months[5];
  const byCode = (totals.data ?? []).filter((r) => r.month === thisMonth && kind(r.category_id) === "expense")
    .map((r) => ({ c: all.find((x) => x.id === r.category_id)!, v: -r.amount_cents })).filter((x) => x.c && x.v > 0).sort((a, b) => b.v - a.v).slice(0, 5);
  const top = Math.max(1, ...byCode.map((x) => Math.max(x.v, x.c.monthly_budget_cents ?? 0)));

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-[17px] font-semibold">Money, last 6 months</h2>
          <p className="mt-0.5 flex gap-4 text-xs text-ink-muted">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: tokenColour("chart-3") }} />in</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: tokenColour("chart-5") }} />spent</span>
          </p>
        </div>
        <Link to="/money/dashboard" className="text-sm font-semibold text-primary">Money</Link>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <div className="flex h-36 items-end gap-3">
            {months.map((m) => (
              <div key={m} className={`flex h-full flex-1 items-end justify-center gap-1 rounded-md px-1 ${m === thisMonth ? "bg-primary/5" : ""}`} title={`${format(parseISO(m), "MMMM")}: in ${money(income(m))}, spent ${money(spend(m))}`}>
                <span className="w-1/3 max-w-5 rounded-t" style={{ height: `${Math.max(income(m) ? 2 : 0, (income(m) / max) * 100)}%`, background: tokenColour("chart-3") }} />
                <span className="w-1/3 max-w-5 rounded-t" style={{ height: `${Math.max(spend(m) ? 2 : 0, (spend(m) / max) * 100)}%`, background: tokenColour("chart-5") }} />
              </div>
            ))}
          </div>
          <div className="mt-1.5 flex gap-3">{months.map((m) => <span key={m} className={`flex-1 text-center text-[11px] ${m === thisMonth ? "font-semibold text-ink" : "text-ink-muted"}`}>{format(parseISO(m), "MMM")}</span>)}</div>
          <div className="mt-3 flex gap-6 border-t border-line pt-3 text-sm">
            <div><div className="num text-xl font-semibold">{money(spend(thisMonth))}</div><div className="text-xs text-ink-muted">spent in {format(now, "MMMM")}</div></div>
            <div><div className="num text-xl font-semibold" style={{ color: tokenColour("chart-3") }}>{money(income(thisMonth))}</div><div className="text-xs text-ink-muted">came in</div></div>
          </div>
        </div>
        <div className="space-y-2.5">
          <div className="text-sm font-medium">Biggest this month</div>
          {byCode.length === 0 ? <p className="text-sm text-ink-muted">Nothing coded for {format(now, "MMMM")} yet. Reconcile the latest CSV and it fills in.</p>
            : byCode.map(({ c, v }) => {
              const b = c.monthly_budget_cents; const over = b !== null && b > 0 && v > b;
              return (
                <div key={c.id} title={b ? `${money(v)} of ${money(b)} budget` : money(v)}>
                  <div className="flex justify-between text-sm"><span className="truncate"><span className="num text-ink-muted">{c.code}</span> {c.name}</span><span className={`num ${over ? "font-semibold text-bad" : ""}`}>{money(v)}{b ? <span className="text-xs text-ink-muted"> / {money(b)}</span> : null}</span></div>
                  <div className="relative mt-1 h-2 overflow-hidden rounded-full bg-surface-raised">
                    <div className="h-full rounded-full" style={{ width: `${(v / top) * 100}%`, background: over ? tokenColour("danger") : tokenColour("chart-5") }} />
                    {b ? <span className="absolute top-0 h-full w-0.5 bg-ink/50" style={{ left: `${Math.min(100, (b / top) * 100)}%` }} /> : null}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </Card>
  );
}
